/**
 * R7.2.2C1 — Single authoritative Telegram BackButton controller.
 * Child routes must NOT register their own BackButton handlers for ordinary nav.
 * Never calls WebApp.close() from child-route back.
 */
import {
  hideTelegramBackButton,
  isStoreV2RootStack,
  syncTelegramBackButton,
  type StoreV2NavView,
} from "./telegram-store-v2-navigation.js";
import { logicalParentOf } from "./customer-navigation-service-v2.js";

let activeHandlerCount = 0;

export function getActiveTelegramBackHandlerCount(): number {
  return activeHandlerCount;
}

export type TelegramBackControllerOptions = {
  stack: StoreV2NavView[];
  /** Close modal/subflow first when true */
  onDismissOverlay?: () => boolean;
  /**
   * Prefer stack pop when previous stack frame is the logical parent;
   * otherwise navigate to logical parent (direct-entry safe).
   */
  onStackBack: () => void;
  onLogicalParent: (route: string) => void;
};

/**
 * Decide whether stack pop lands on the declared logical parent.
 */
export function shouldUseLogicalParentFallback(
  stack: StoreV2NavView[],
): { useLogical: boolean; route: string } {
  if (isStoreV2RootStack(stack)) return { useLogical: false, route: "home" };
  const top = stack[stack.length - 1];
  const route = String(top?.name ?? "home");
  const parent = logicalParentOf(route);
  if (!parent) return { useLogical: false, route };
  const prev = stack[stack.length - 2];
  const prevName = String(prev?.name ?? "");
  // Direct entry / thin stack: previous frame is home (or missing) but logical parent is not home
  if (parent !== "home" && parent !== "documents-home" && prevName !== parent) {
    return { useLogical: true, route };
  }
  return { useLogical: false, route };
}

/**
 * Mount exactly one BackButton listener for the current stack.
 * Returns cleanup that decrements the handler counter.
 */
export function mountTelegramBackController(options: TelegramBackControllerOptions): () => void {
  const handler = () => {
    if (options.onDismissOverlay?.()) return;
    if (isStoreV2RootStack(options.stack)) return;
    const decision = shouldUseLogicalParentFallback(options.stack);
    if (decision.useLogical) {
      options.onLogicalParent(decision.route);
      return;
    }
    options.onStackBack();
  };

  activeHandlerCount += 1;
  const cleanupSync = syncTelegramBackButton({
    stack: options.stack,
    onBack: handler,
  });

  return () => {
    cleanupSync();
    activeHandlerCount = Math.max(0, activeHandlerCount - 1);
    if (isStoreV2RootStack(options.stack)) {
      hideTelegramBackButton();
    }
  };
}

/** Safe chrome/UI back — same policy as Telegram Back; never WebApp.close. */
export function resolveChromeBackAction(
  stack: StoreV2NavView[],
  hasCabinetHistoryState: boolean,
): "cabinet_history" | "stack_back" | "logical_parent" | "noop_root" {
  if (isStoreV2RootStack(stack)) return "noop_root";
  if (hasCabinetHistoryState) {
    // Only consume pushState panels when history has depth beyond the WebView entry
    try {
      if (typeof window !== "undefined" && window.history.length > 1) {
        return "cabinet_history";
      }
    } catch {
      /* fall through */
    }
  }
  const decision = shouldUseLogicalParentFallback(stack);
  return decision.useLogical ? "logical_parent" : "stack_back";
}
